/**
 * Derives the UNGM-style 5-stage onboarding progress for the current user.
 *
 * Pure by design (docs/onboarding-spec.md §8.2) — no redux, no fetching. Callers
 * (the onboarding duck, the stepper) hand in already-loaded data:
 *   currentUser     the currentUser API entity
 *   profileListing  the provider's profile ownListing entity, or null/undefined
 *   config          the app's merged configuration (config.listing, config.user,
 *                    config.categoryConfiguration, config.accessControl)
 *
 * Providers ride the listing-approval gate; food companies have no listing, so
 * their gate is the equivalent primitive one level up on the user's extended
 * data. See docs/onboarding-spec.md §2-§4 for the full derivation and the
 * product decisions ("GUESS" tags) behind isProfileComplete.
 */
import {
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_LONG,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
} from './types';
import { isFieldForCategory, isFieldForListingType, pickCategoryFields } from './fieldHelpers';
import { initialValuesForUserFields, addScopePrefix, isUserFieldHidden } from './userHelpers';
import { requireListingImage } from './configHelpers';
import {
  documentsForUser,
  missingRequiredDocuments,
  DOCUMENTS_PRIVATE_DATA_KEY,
} from '../config/configProfileDocuments';

export const ONBOARDING_STAGE_ACCOUNT_CREATED = 1;
export const ONBOARDING_STAGE_ACCOUNT_ACTIVATED = 2;
export const ONBOARDING_STAGE_REGISTRATION_STARTED = 3;
export const ONBOARDING_STAGE_REGISTRATION_COMPLETE = 4;
export const ONBOARDING_STAGE_REGISTRATION_SUBMITTED = 5;

export const ONBOARDING_STAGES = [
  ONBOARDING_STAGE_ACCOUNT_CREATED,
  ONBOARDING_STAGE_ACCOUNT_ACTIVATED,
  ONBOARDING_STAGE_REGISTRATION_STARTED,
  ONBOARDING_STAGE_REGISTRATION_COMPLETE,
  ONBOARDING_STAGE_REGISTRATION_SUBMITTED,
];

const SERVICE_PROVIDER = 'service-provider';

// The signup form only ever writes these three publicData keys (getNonUserFieldParams
// in AuthenticationPage.helpers.js). Anything beyond them means the member has
// touched their profile since — see docs/onboarding-spec.md §4.2.
const SIGNUP_KEYS = ['userType', 'business_name', 'country'];

const emptyResult = (stage, blockedReason, isApproved = false) => ({
  stage,
  isApproved,
  missingFields: [],
  missingDocuments: [],
  blockedReason,
  willBeReviewed: false,
});

/**
 * The provider profile's cover image, reported through `missingFields` like a
 * required field so the hub's checklist names it and "Continue registration"
 * deep-links to the tab that holds it. `panel` is the wizard tab id that
 * EditListingWizardTab exports as PHOTOS; the literal keeps util/ free of
 * container imports.
 */
export const COVER_IMAGE_FIELD_KEY = 'coverImage';
const MISSING_COVER_IMAGE_FIELD = {
  key: COVER_IMAGE_FIELD_KEY,
  panel: 'photos',
  saveConfig: { label: 'Cover image' },
};

// ---- Provider: which of the 22 listing fields (docs §3.2) are still missing ----

const isListingFieldSatisfied = (fieldConfig, publicData, privateData) => {
  const { key, schemaType, enumOptions = [], scope } = fieldConfig;
  const fieldData = (scope === 'private' ? privateData : publicData) || {};
  const value = fieldData[key];
  const optionKeys = enumOptions.map(o => `${o.option}`);

  switch (schemaType) {
    case 'enum':
      return typeof value === 'string' && optionKeys.includes(value);
    case SCHEMA_TYPE_MULTI_ENUM:
      return Array.isArray(value) && value.length > 0 && value.every(v => optionKeys.includes(v));
    case 'shortText':
    case 'text':
      return typeof value === 'string' && value.length > 0;
    case SCHEMA_TYPE_LONG:
      return typeof value === 'number' && Number.isInteger(value);
    case SCHEMA_TYPE_BOOLEAN:
      return value === true || value === false;
    case 'youtubeVideoUrl':
      return typeof value === 'string';
    default:
      return false;
  }
};

const missingRequiredListingFields = (publicData, privateData, config) => {
  const listingFields = config?.listing?.listingFields || [];
  const categoryKey = config?.categoryConfiguration?.key;
  const categoryOptions = config?.categoryConfiguration?.categories;
  const categoriesObj = pickCategoryFields(publicData, categoryKey, 1, categoryOptions);
  const currentCategories = Object.values(categoriesObj);

  return listingFields.filter(fieldConfig => {
    if (!fieldConfig?.saveConfig?.isRequired) return false;
    const isTargetListingType = isFieldForListingType(publicData?.listingType, fieldConfig);
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    if (!isTargetListingType || !isTargetCategory) return false;
    return !isListingFieldSatisfied(fieldConfig, publicData, privateData);
  });
};

// ---- Food company: which required user fields (Task 5, configUser.js) are missing ----

const isUserFieldValueFilled = (schemaType, value) => {
  if (value == null) return false;
  switch (schemaType) {
    case SCHEMA_TYPE_MULTI_ENUM:
      return Array.isArray(value) && value.length > 0;
    case SCHEMA_TYPE_BOOLEAN:
      return value === true || value === false;
    case SCHEMA_TYPE_LONG:
      return typeof value === 'number' && Number.isInteger(value);
    default:
      return typeof value === 'string' && value.length > 0;
  }
};

// Vacuously returns [] until Task 5 sets isRequired on a food-company field —
// see docs/onboarding-spec.md §4.2. Reuses initialValuesForUserFields rather
// than writing a third traversal over configUser.js.
//
// Exported so EditListingWizard can run the exact same check before letting a
// provider publish — see EditListingWizard.js's `missingContactFields`. Before
// that wiring, this function only fed the onboarding hub's informational
// checklist, which nothing enforced: a provider could complete every wizard
// tab and hit "Publish" with job title still missing (client feedback
// 2026-09-18, item 1).
export const missingRequiredUserFields = ({
  publicData,
  protectedData,
  privateData,
  userType,
  userFields,
}) => {
  const fields = userFields || [];
  const values = {
    ...initialValuesForUserFields(publicData || {}, 'public', userType, fields),
    ...initialValuesForUserFields(protectedData || {}, 'protected', userType, fields),
    ...initialValuesForUserFields(privateData || {}, 'private', userType, fields),
  };
  return fields.filter(field => {
    if (!field?.saveConfig?.isRequired) return false;
    const isTargetUserType =
      !field.userTypeConfig?.limitToUserTypeIds ||
      field.userTypeConfig?.userTypeIds?.includes(userType);
    if (!isTargetUserType) return false;
    // A conditional field (`showWhen`) the member cannot currently see is not
    // something they can be asked to fill in.
    if (isUserFieldHidden(field, values, fields)) return false;
    const namespacedKey = addScopePrefix(field.scope || 'public', field.key);
    return !isUserFieldValueFilled(field.schemaType, values[namespacedKey]);
  });
};

// ---- Provider stage (docs §3) ----

const getProviderStage = ({ currentUser, profileListing, config }) => {
  if (!currentUser?.id) {
    return emptyResult(0, 'no-user');
  }

  const listing = profileListing?.attributes?.deleted !== true ? profileListing : null;
  const state = listing?.id ? listing.attributes?.state : null;
  const willBeReviewed = !!config?.accessControl?.listings?.requireApprovalToPublish;

  // A listing that reached pendingApproval/published/closed proves every
  // earlier stage already happened — checked BEFORE emailVerified on purpose.
  // Sharetribe enforces nothing today (docs/onboarding-spec.md §0: "the
  // account walked the whole flow unverified"), so a live, already-published
  // profile must not read as stage 1 just because its owner never verified.
  if (
    [LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_PUBLISHED, LISTING_STATE_CLOSED].includes(state)
  ) {
    return {
      stage: ONBOARDING_STAGE_REGISTRATION_SUBMITTED,
      isApproved: state === LISTING_STATE_PUBLISHED,
      missingFields: [],
      missingDocuments: [],
      blockedReason: null,
      willBeReviewed,
    };
  }

  if (!currentUser.attributes?.emailVerified) {
    return emptyResult(ONBOARDING_STAGE_ACCOUNT_CREATED, 'email-not-verified');
  }

  if (!listing?.id) {
    return emptyResult(ONBOARDING_STAGE_ACCOUNT_ACTIVATED, 'not-started');
  }

  // draft, or any other/unknown listing state — still in progress.
  const { publicData = {}, privateData = {} } = listing.attributes || {};
  const userPublicData = currentUser.attributes?.profile?.publicData || {};
  const userProtectedData = currentUser.attributes?.profile?.protectedData || {};
  const userPrivateData = currentUser.attributes?.profile?.privateData || {};
  const documentSlots = userPrivateData[DOCUMENTS_PRIVATE_DATA_KEY] || {};

  // A provider's registration is split across two records: Form 1 §02-§07 live
  // on the profile LISTING, but §01 Contact Information lives on the USER — and
  // §01 marks Job title as required. This gate used to check the listing only,
  // so a provider could reach "Ready to submit" and publish without ever being
  // asked for a job title (client feedback 2026-09-16, item 1: "I am able to
  // continue the form without filling required field (service providers)" —
  // found on the dev deployment, not in a test).
  //
  // User-field gaps are listed FIRST, matching the document's order (§01 before
  // §02), and tagged so the onboarding hub can send the member to the page that
  // actually holds the field: a user field has no wizard `panel`, and falling
  // back to the Details tab would deep-link them somewhere it is not.
  const missingUserFields = missingRequiredUserFields({
    publicData: userPublicData,
    protectedData: userProtectedData,
    privateData: userPrivateData,
    userType: userPublicData.userType,
    userFields: config?.user?.userFields,
  }).map(field => ({ ...field, isUserField: true }));

  // The cover image is the one required part of the profile that is neither a
  // listing field nor a document: it is the listing's `images` relationship.
  // The wizard's Photos tab refuses to publish without one, but the hub's
  // "Submit for vetting" publishes the draft directly and never looked, so a
  // provider who had done every tab except Photos could still submit (client
  // feedback 2026-10-07, item 3: "Service providers should not be able to
  // submit their application without uploading the required image"). Listed
  // last: it is the last tab, and the checklist deep-links to its first entry.
  const listingTypeConfig = (config?.listing?.listingTypes || []).find(
    t => t.listingType === publicData.listingType
  );
  const hasCoverImage = (listing.images || []).length > 0;
  const missingImage =
    requireListingImage(listingTypeConfig) && !hasCoverImage ? [MISSING_COVER_IMAGE_FIELD] : [];

  const missingFields = [
    ...missingUserFields,
    ...missingRequiredListingFields(publicData, privateData, config),
    ...missingImage,
  ];
  const missingDocuments = missingRequiredDocuments(
    documentsForUser({ userType: userPublicData.userType, country: userPublicData.country }),
    documentSlots
  );
  const isComplete = missingFields.length === 0 && missingDocuments.length === 0;

  return {
    stage: isComplete
      ? ONBOARDING_STAGE_REGISTRATION_COMPLETE
      : ONBOARDING_STAGE_REGISTRATION_STARTED,
    isApproved: false,
    missingFields,
    missingDocuments,
    blockedReason: isComplete ? null : 'incomplete',
    willBeReviewed,
  };
};

// ---- Food company stage (docs §4) ----

const getFoodProcessorStage = ({ currentUser, config }) => {
  if (!currentUser?.id) {
    return emptyResult(0, 'no-user');
  }

  const profile = currentUser.attributes?.profile || {};
  const { publicData = {}, protectedData = {}, privateData = {}, metadata = {} } = profile;
  const userType = publicData.userType;
  const documentSlots = privateData[DOCUMENTS_PRIVATE_DATA_KEY] || {};
  const isApproved = metadata?.approved === true;
  // Food companies have no listing-approval toggle to degrade against — the
  // operator hand-setting `metadata.approved` is the only review path.
  const willBeReviewed = true;

  // Same ordering fix as the provider path: a submitted registration proves
  // email was effectively usable at submit time, so check it before the
  // emailVerified flag rather than letting a since-changed flag hide it.
  if (protectedData?.registration?.submittedAt) {
    return {
      stage: ONBOARDING_STAGE_REGISTRATION_SUBMITTED,
      isApproved,
      missingFields: [],
      missingDocuments: [],
      blockedReason: null,
      willBeReviewed,
    };
  }

  if (!currentUser.attributes?.emailVerified) {
    return emptyResult(ONBOARDING_STAGE_ACCOUNT_CREATED, 'email-not-verified');
  }

  const missingFields = missingRequiredUserFields({
    publicData,
    protectedData,
    privateData,
    userType,
    userFields: config?.user?.userFields,
  });
  const missingDocuments = missingRequiredDocuments(
    documentsForUser({ userType, country: publicData.country }),
    documentSlots
  );

  const hasStartedRegistration =
    Object.keys(publicData).length > SIGNUP_KEYS.length || Object.keys(documentSlots).length > 0;

  if (!hasStartedRegistration) {
    return {
      stage: ONBOARDING_STAGE_ACCOUNT_ACTIVATED,
      isApproved,
      missingFields,
      missingDocuments,
      blockedReason: 'not-started',
      willBeReviewed,
    };
  }

  const isComplete = missingFields.length === 0 && missingDocuments.length === 0;
  return {
    stage: isComplete
      ? ONBOARDING_STAGE_REGISTRATION_COMPLETE
      : ONBOARDING_STAGE_REGISTRATION_STARTED,
    isApproved,
    missingFields,
    missingDocuments,
    blockedReason: isComplete ? null : 'incomplete',
    willBeReviewed,
  };
};

/**
 * Where a member who still has registration to do should be sent.
 *
 * Client feedback 2026-09-11: "it would be helpful if new sign-ups were
 * directed straight to the form after completing registration, rather than
 * being redirected to the website." Signup and email-verification both used to
 * land on the onboarding hub, which is one more click and one more page of
 * reading before anyone sees a field — and before that, upstream, the landing
 * page, which says nothing about registration at all.
 *
 * The hub is still the destination at stage 1, and only there: the member has
 * not verified their email yet, so the hub's resend-verification panel is the
 * only useful screen. Everyone else goes straight to their form.
 *
 * @param {Object} params
 * @param {string} params.userType
 * @param {number} params.stage from getOnboardingStage()
 * @returns {{name: string}} a route descriptor for NamedRedirect / NamedLink
 */
export const getRegistrationEntryRoute = ({ userType, stage }) => {
  if (stage < ONBOARDING_STAGE_ACCOUNT_ACTIVATED) {
    return { name: 'OnboardingPage' };
  }
  return userType === SERVICE_PROVIDER
    ? // A provider's form is the listing wizard. NewListingPage redirects to the
      // existing draft when there is one, so this resumes rather than restarts.
      { name: 'NewListingPage' }
    : { name: 'ProfileSettingsPage' };
};

/**
 * @param {Object} params
 * @param {Object} params.currentUser
 * @param {Object} [params.profileListing] the provider's profile ownListing entity
 * @param {Object} params.config
 * @returns {{stage: number, isApproved: boolean, missingFields: Array, missingDocuments: Array, blockedReason: string|null, willBeReviewed: boolean}}
 */
export const getOnboardingStage = ({ currentUser, profileListing, config }) => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  return userType === SERVICE_PROVIDER
    ? getProviderStage({ currentUser, profileListing, config })
    : getFoodProcessorStage({ currentUser, config });
};
